import { RATES, type RateRow } from "@/lib/villa-content";

export type Beds = 2 | 3 | 4;

export type ParsedRate = {
  start: Date; // inclusive
  end: Date;   // inclusive
  label: string;
  beds2: number | null;
  beds3: number | null;
  beds4: number | null;
  water2: number | null;
  water3: number | null;
  water4: number | null;
};

function parseEU(d: string): Date {
  // "dd.mm.yyyy"
  const [day, month, year] = d.trim().split(".").map(Number);
  return new Date(year, month - 1, day);
}

function parsePeriod(period: string): { start: Date; end: Date } {
  const [s, e] = period.split(/[-–]/).map((p) => p.trim());
  return { start: parseEU(s), end: parseEU(e) };
}

export function parseRates(rows: RateRow[]): ParsedRate[] {
  const seen = new Set<string>();
  return rows
    .filter((r) => {
      const key = r.period.trim();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .map((r) => {
    const { start, end } = parsePeriod(r.period);
    return {
      start,
      end,
      label: r.period,
      beds2: r.beds2,
      beds3: r.beds3,
      beds4: r.beds4,
      water2: r.water2 ?? null,
      water3: r.water3 ?? null,
      water4: r.water4 ?? null,
    };
  });
}

/** Default rates parsed from the static fallback in villa-content. */
export const PARSED_RATES: ParsedRate[] = parseRates(RATES);

function priceFor(rate: ParsedRate, beds: Beds): number | null {
  if (beds === 2) return rate.beds2;
  if (beds === 3) return rate.beds3;
  return rate.beds4;
}

function startOfDay(d: Date): Date {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

function addDays(d: Date, n: number): Date {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}

export type QuoteSegment = {
  label: string;
  nights: number;
  weeklyRate: number | null;
  subtotal: number | null; // null when bed config not available in that period
};

export type Quote = {
  nights: number;
  segments: QuoteSegment[];
  weeklyAverage: number | null; // for display when single period
  rentalSubtotal: number | null;
  outsideRateCard: number; // nights with no matching period
  discount?: {
    label: string;
    percent: number;
    amount: number;
  } | null;
};

/**
 * Compute a quote for a half-open date range [checkIn, checkOut).
 * Splits nights across rate periods and prorates weekly rate by nights/7.
 */
export function computeQuote(
  checkIn: Date,
  checkOut: Date,
  beds: Beds,
  rates: ParsedRate[] = PARSED_RATES,
): Quote {
  const start = startOfDay(checkIn);
  const end = startOfDay(checkOut);
  const totalNights = Math.max(0, Math.round((end.getTime() - start.getTime()) / 86_400_000));
  if (totalNights === 0) {
    return { nights: 0, segments: [], weeklyAverage: null, rentalSubtotal: 0, outsideRateCard: 0, discount: null };
  }

  // Bucket each night by the period that contains it.
  const counts = new Map<string, number>();
  let outside = 0;
  for (let i = 0; i < totalNights; i++) {
    const night = addDays(start, i);
    const found = rates.find((r) => night >= r.start && night <= r.end);
    if (!found) {
      outside++;
      continue;
    }
    counts.set(found.label, (counts.get(found.label) ?? 0) + 1);
  }

  const usedLabels = new Set<string>();
  const segments: QuoteSegment[] = rates
    .filter((r) => {
      if (!counts.has(r.label) || usedLabels.has(r.label)) return false;
      usedLabels.add(r.label);
      return true;
    })
    .map((r) => {
    const nights = counts.get(r.label)!;
    const weekly = priceFor(r, beds);
    const subtotal = weekly == null ? null : Math.round((weekly / 7) * nights);
    return { label: r.label, nights, weeklyRate: weekly, subtotal };
  });

  // Nights that fall outside every rate period have no known price: show the
  // rental as "on request" instead of silently pricing them at 0.
  const anyNull = segments.some((s) => s.subtotal == null);
  const rentalSubtotal =
    anyNull || outside > 0
      ? null
      : segments.reduce((sum, s) => sum + (s.subtotal ?? 0), 0);

  const weeklyAverage =
    segments.length === 1 ? segments[0].weeklyRate : null;

  return {
    nights: totalNights,
    segments,
    weeklyAverage,
    rentalSubtotal,
    outsideRateCard: outside,
    discount: null,
  };
}

/**
 * Apply a last-minute discount to a quote (mutates a copy).
 * Discount applies when check-in is within `withinDays` of today and quote has a rental subtotal.
 */
export function applyLastMinuteDiscount(
  quote: Quote,
  checkIn: Date,
  opts: { enabled: boolean; percent: number; withinDays: number; label: string },
): Quote {
  if (!opts.enabled || opts.percent <= 0 || quote.rentalSubtotal == null || quote.nights === 0) return quote;
  const today = startOfDay(new Date());
  const ci = startOfDay(checkIn);
  const diffDays = Math.round((ci.getTime() - today.getTime()) / 86_400_000);
  if (diffDays < 0 || diffDays > opts.withinDays) return quote;
  const amount = Math.round((quote.rentalSubtotal * opts.percent) / 100);
  return {
    ...quote,
    discount: { label: opts.label, percent: opts.percent, amount },
  };
}

/** Fixed extras shown alongside every quote. */
export const FIXED_EXTRAS = {
  cleaning: 230,
  linenPerPerson: 10,
  deposit: 500, // refundable
  babyPack: 30, // optional
};

export function formatEUR(n: number): string {
  return `€${n.toLocaleString("en-GB")}`;
}

/** Format a Date as a local YYYY-MM-DD string. */
export function toLocalISO(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export function parseLocalISO(s: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return Number.isNaN(d.getTime()) ? null : d;
}

function waterFor(rate: ParsedRate, beds: Beds): number | null {
  if (beds === 2) return rate.water2;
  if (beds === 3) return rate.water3;
  return rate.water4;
}

/**
 * Sum the included m³ of water across the stay, prorating each period's
 * weekly allowance by nights/7. Returns the rounded total (to 1 decimal).
 */
export function computeIncludedWaterM3(
  checkIn: Date,
  checkOut: Date,
  beds: Beds,
  rates: ParsedRate[] = PARSED_RATES,
): number {
  const start = startOfDay(checkIn);
  const end = startOfDay(checkOut);
  const totalNights = Math.max(0, Math.round((end.getTime() - start.getTime()) / 86_400_000));
  if (totalNights === 0) return 0;
  const counts = new Map<string, number>();
  for (let i = 0; i < totalNights; i++) {
    const night = addDays(start, i);
    const found = rates.find((r) => night >= r.start && night <= r.end);
    if (!found) continue;
    counts.set(found.label, (counts.get(found.label) ?? 0) + 1);
  }
  let total = 0;
  for (const [label, nights] of counts) {
    const rate = rates.find((r) => r.label === label);
    if (!rate) continue;
    const weekly = waterFor(rate, beds);
    if (weekly == null) continue;
    total += (weekly / 7) * nights;
  }
  return Math.round(total * 10) / 10;
}